import React, { useCallback, useEffect, useState } from 'react'
import { Button, H2 } from '../../lib/components'
import { tradingAdminServices } from '../../services'
import { ClientCell } from '../wallet-topups/client-cell'

type Attention = 'HELD' | 'IN_PROGRESS' | 'STUCK' | 'REFUND_DUE' | 'CREDIT_DUE'
type Action = 'refresh' | 'cancel' | 'resolve'

export type AttentionTransfer = {
  id: number
  alpacaJournalId: string | null
  investorId: number
  investorName: string | null
  investorReference: string | null
  direction: 'DEPOSIT' | 'WITHDRAWAL'
  amountCents: number
  status: string
  attention: Attention
  cancellable: boolean
  error: string | null
  createdAt: string
}

const ATTENTION: Record<Attention, { label: string; help: string }> = {
  HELD: {
    label: 'Held for review',
    help: 'Alpaca is holding this journal, usually because it is over the journal limit.',
  },
  IN_PROGRESS: {
    label: 'In progress',
    help: 'Accepted by Alpaca and still processing.',
  },
  STUCK: {
    label: 'Not confirmed',
    help: 'Alpaca never confirmed this transfer. Resolve looks it up at Alpaca.',
  },
  REFUND_DUE: {
    label: 'Refund due',
    help: 'The deposit failed but the wallet has not been refunded yet. Refresh applies it.',
  },
  CREDIT_DUE: {
    label: 'Credit due',
    help: 'The withdrawal completed but the wallet has not been credited yet. Refresh applies it.',
  },
}

const CONFIRM: Partial<Record<Action, string>> = {
  cancel:
    'Cancel this transfer at Alpaca? A deposit is refunded to the investor wallet; a withdrawal leaves the cash in the brokerage account.',
  resolve:
    'Resolve this transfer? It is looked up at Alpaca by its description. If found, it is linked and applied; if not, it is marked failed and a deposit is refunded to the investor wallet.',
}

const DIRECTION = { DEPOSIT: 'Wallet → brokerage', WITHDRAWAL: 'Brokerage → wallet' }

const usd = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/** Wallet <-> brokerage transfers an admin may need to act on, across all dates. */
const TransfersNeedingAttention = () => {
  const [rows, setRows] = useState<AttentionTransfer[]>([])
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<{ id: number; action: Action } | null>(null)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const load = useCallback(async () => {
    const res = await tradingAdminServices.listTransfersNeedingAttention()
    if (res?.status === 200) setRows(res.data)
    else setMessage({ ok: false, text: 'Could not load transfers needing attention.' })
    setLoaded(true)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const run = async (id: number, action: Action) => {
    setBusy(`${id}:${action}`)
    setConfirming(null)
    setMessage(null)
    const res = await tradingAdminServices.transferAction(id, action)
    setBusy(null)
    if (res?.status === 200) {
      setMessage({ ok: true, text: `Transfer #${id}: now ${res.data.status}.` })
    } else {
      setMessage({
        ok: false,
        text: res?.data?.error?.message || `Could not ${action} transfer #${id}.`,
      })
    }
    void load()
  }

  const ask = (id: number, action: Action) =>
    CONFIRM[action] ? setConfirming({ id, action }) : void run(id, action)

  const actionsFor = (row: AttentionTransfer): Array<{ action: Action; text: string }> => {
    const actions: Array<{ action: Action; text: string }> = []
    if (row.attention === 'STUCK') actions.push({ action: 'resolve', text: 'Resolve' })
    else actions.push({ action: 'refresh', text: 'Refresh' })
    if (row.cancellable) actions.push({ action: 'cancel', text: 'Cancel at Alpaca' })
    return actions
  }

  return (
    <section className='funding-recon'>
      <H2 text='Transfers needing attention' classes={['mb-24']} />
      <p className='funding-recon__intro'>
        Wallet ↔ brokerage transfers that are held by Alpaca, never confirmed, or whose result has
        not reached the investor wallet yet.
      </p>
      {message ? (
        <p role='status' className={message.ok ? 'funding-recon__notice' : 'funding-recon__error'}>
          {message.text}
        </p>
      ) : null}
      {!loaded ? null : rows.length === 0 ? (
        <p className='wallet-admin__empty'>Nothing needs attention.</p>
      ) : (
        <div className='w-100 table_scroll_horizontal'>
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Transfer</th>
                <th>Client</th>
                <th>Direction</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Needs</th>
                <th>Requested</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    #{row.id}
                    {row.alpacaJournalId ? <small>{row.alpacaJournalId}</small> : null}
                  </td>
                  <td>
                    <ClientCell name={row.investorName} reference={row.investorReference} />
                  </td>
                  <td>{DIRECTION[row.direction]}</td>
                  <td>{usd(row.amountCents)}</td>
                  <td>
                    {row.status}
                    {row.error ? <small>{row.error}</small> : null}
                  </td>
                  <td title={ATTENTION[row.attention].help}>{ATTENTION[row.attention].label}</td>
                  <td>{new Date(row.createdAt).toLocaleString()}</td>
                  <td>
                    {confirming?.id === row.id ? (
                      <div className='funding-recon__confirm'>
                        <p>{CONFIRM[confirming.action]}</p>
                        <Button
                          text='Confirm'
                          variant='primary-btn'
                          handleClick={() => void run(row.id, confirming.action)}
                        />
                        <Button
                          text='Back'
                          variant='secondary-btn'
                          handleClick={() => setConfirming(null)}
                        />
                      </div>
                    ) : (
                      <div className='wallet-admin__actions'>
                        {actionsFor(row).map(({ action, text }) => (
                          <Button
                            key={action}
                            text={busy === `${row.id}:${action}` ? 'Working…' : text}
                            variant={action === 'refresh' ? 'secondary-btn' : 'primary-btn'}
                            isDisabled={busy !== null}
                            handleClick={() => ask(row.id, action)}
                          />
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export { TransfersNeedingAttention }
